import { useEffect, useMemo, useState } from "react";

import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";

import classNames from "classnames/bind";
import ComboFilters from "../ComboFilters";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import { Hairline, Loading } from "@components";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { Trend } from "../Reports";
import { useDrag } from "shared/hooks/useDrag";

const css = classNames.bind(styles);

const filters = [
  { name: "Qtd. Pedidos", key: "order_quantity", type: "int" },
  { name: "Faturamento total", key: "order_total", type: "currency" },
  { name: "Taxa de frete", key: "delivery_total", type: "currency" },
  { name: "Taxa de serviço", key: "service_total", type: "currency" },
  {
    name: "Faturamento sem taxas",
    key: "total_without_delivery_and_service",
    type: "currency",
  },
  { name: "Ticket médio", key: "average_order_value", type: "currency" },
  { name: "Pedidos p/cliente", key: "orders_per_client", type: "float" },
  { name: "Produtos p/cliente", key: "products_per_order", type: "float" },
];

const getFilterName = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.name : key;
};

const getFilterType = (key) => {
  const filter = filters.find((f) => f.key === key);
  return filter ? filter.type : "string";
};

const getFormatedValue = (key, value) => {
  if (getFilterType(key) === "currency") {
    return Currency.formatCurrency(value, false, 2);
  } else if (getFilterType(key) === "int") {
    return Numbers.formatInt(value);
  } else if (getFilterType(key) === "float") {
    return Numbers.formatFloat(value, 2);
  }
  return value;
};

export default function OrdersDistribution({ data, hasReportFilters }) {
  const [showAll, setShowAll] = useState("see_details");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("order_quantity");
  const [label_dict, setLabelDict] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [data]);

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("orders-distribution-chart");
      if (!chartDOM) return;

      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const getChartData = () => {
        const distribution = (data?.[selectedOption?.id || "units"] || [])?.map(
          (item) => ({
            name:
              label_dict.get(
                `${selectedOption?.id || "units"}_${item.label}`
              ) || item.label,
            value: item[selectedFilter]?.value || 0,
          })
        );

        return distribution;
      };

      const options = {
        tooltip: {
          trigger: "item",
          responsive: true,
          formatter: (param) => {
            const data = param.data;
            return [
              `<span>${data.name}</span>
							<br/>
							<span style="opacity: 50%">
								${getFormatedValue(selectedFilter, data.value)}								
							</span>
						`,
            ].join("");
          },
          backgroundColor: "#242424",
          borderWidth: "0",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 10,
          },
        },
        series: [
          {
            type: "pie",
            radius: ["75%"],
            center: ["50%", "45%"],
            label: {
              show: false,
              position: "center",
            },
            data: getChartData(),
          },
        ],
        legend: {
          type: "scroll",
          orient: "horizontal",
          pageIconColor: "#6D6DF2",
          pageIconInactiveColor: "#4C4C4C",
          backgroundColor: "#1C1C1C",
          borderRadius: 10,
          padding: 20,
          pageIconSize: 10,
          pageTextStyle: {
            color: "#B3B3B3",
            fontSize: 10,
            fontWeight: 800,
          },
          pageIcons: {
            horizontal: [
              "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
              "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
            ],
          },
          top: "bottom",
          icon: "circle",
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 10,
          textStyle: {
            color: "#808080",
            fontSize: 12,
          },
          inactiveColor: "#555",
          selected: Object.fromEntries(
            getChartData().map((item, index) => [item.name, index < 5])
          ),
        },
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedFilter, showAll, label_dict]);

  useEffect(() => {
    scrollRef.current.scrollTo(0, 0);
  }, [selectedFilter]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const sortByOrderQuantity = (arr) =>
    arr.sort(
      (a, b) => Number(b.order_quantity.value) - Number(a.order_quantity.value)
    );

  data.brands = sortByOrderQuantity(data?.brands || []);
  data.units = sortByOrderQuantity(data?.units || []);
  data.origins = sortByOrderQuantity(data?.origins || []);

  const scrollFiltersRef = useDrag(null);

  return (
    <div className={styles.box2} style={{ minHeight: 632 }}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Distribuição de pedidos</p>
          <span
            className={styles.info}
            title="Distribuição dos dados de pedidos por unidade, marca e origem."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        {!loading && (
          <div className="d-flex align-center">
            <div className="d-flex align-center gap-10">
              <div
                className={css({
                  selectMini: true,
                  selected: showAll === "see_details",
                })}
                onClick={() => setShowAll("see_details")}
              >
                Ver detalhes
              </div>
              <div
                className={css({
                  selectMini: true,
                  selected: showAll === "see_all",
                })}
                onClick={() => setShowAll("see_all")}
              >
                Ver tudo
              </div>
            </div>
          </div>
        )}
      </div>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={showAll === "see_details"}>
        <div className="mb-15 mt-15">
          <div className={styles.boxFilters} ref={scrollFiltersRef}>
            {filters.map((filter) => (
              <div
                key={filter.key}
                className={css({
                  filter: true,
                  selected: selectedFilter === filter.key,
                })}
                onClick={() => setSelectedFilter(filter.key)}
              >
                {filter.name}
              </div>
            ))}
          </div>
        </div>
      </If>

      <If cond={showAll === "see_all"}>
        <Hairline className="mt-15" />
      </If>

      <div className={styles.container}>
        <If cond={showAll === "see_details"}>
          <div className="px-20 d-flex w-100">
            <div
              className={styles.tableOrdersDistribution}
              onMouseDown={onMouseDown}
              ref={scrollRef}
            >
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex">
                        <ComboFilters
                          options={[
                            { name: "Unidades", id: "units" },
                            { name: "Marcas", id: "brands" },
                            { name: "Origens", id: "origins" },
                          ]}
                          design="two"
                          onSelect={(option) => setSelectedOption(option)}
                          selectedOption={
                            selectedOption || {
                              name: "Unidades",
                              id: "units",
                            }
                          }
                        />
                      </div>
                    </th>
                    <th style={{ width: 200 }}>
                      {getFilterName(selectedFilter || "order_quantity")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          {label_dict.get(
                            `${selectedOption?.id || "units"}_${item.label}`
                          ) || item.label}
                        </td>
                        <td>
                          <div className="d-flex align-center">
                            <p className={styles.value}>
                              {getFormatedValue(
                                selectedFilter,
                                item[selectedFilter]?.value
                              )}
                            </p>
                            {/* <If cond={!hasReportFilters}>
                              <Trend growth={item[selectedFilter]?.growth} />
                            </If> */}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.chartContainer}>
              <p className={styles.title}>
                Proporção de {getFilterName(selectedFilter || "order_quantity")}
              </p>
              <div
                id="orders-distribution-chart"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 350,
                  margin: "auto",
                }}
              />
            </div>
          </div>
        </If>

        <If cond={showAll === "see_all"}>
          <div
            className={styles.tableOrdersDistributionFull}
            onMouseDown={onMouseDown}
            ref={scrollRef}
          >
            <table>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      backgroundColor: "#1f1f1f",
                      minWidth: 200,
                    }}
                  >
                    <div className="d-flex">
                      <ComboFilters
                        options={[
                          { name: "Unidades", id: "units" },
                          { name: "Marcas", id: "brands" },
                          { name: "Origens", id: "origins" },
                        ]}
                        design="two"
                        onSelect={(option) => setSelectedOption(option)}
                        selectedOption={
                          selectedOption || {
                            name: "Unidades",
                            id: "units",
                          }
                        }
                      />
                    </div>
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    QTD. DE PEDIDOS
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    FATURAMENTO TOTAL
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    TAXA DE FRETE
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    TAXA DE SERVIÇO
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    FATURAMENTO SEM TAXAS
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    TICKET MÉDIO
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    MÉDIA PEDIDOS POR CLIENTE
                  </th>
                  <th style={{ paddingLeft: 35, minWidth: 230 }}>
                    MÉDIA PRODUTOS POR PEDIDO
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#1c1c1c",
                        }}
                      >
                        {label_dict.get(
                          `${selectedOption?.id || "units"}_${item.label}`
                        ) || item.label}
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item?.order_quantity.value)}
                          </p>
                          {/* <Trend
                            growth={item?.order_quantity?.growth || null}
                          /> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.order_total?.value,
                              false,
                              2
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend growth={item?.order_total?.growth || null} />
                          </If> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.delivery_total?.value
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend
                              growth={item?.delivery_total?.growth || null}
                            />
                          </If> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.service_total?.value
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend
                              growth={item?.service_total?.growth || null}
                            />
                          </If> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.total_without_delivery_and_service?.value
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend
                              growth={
                                item?.total_without_delivery_and_service
                                  ?.growth || null
                              }
                            />
                          </If> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Currency.formatCurrency(
                              item?.average_order_value?.value
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend
                              growth={item?.average_order_value?.growth || null}
                            />
                          </If> */}
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item?.orders_per_client?.value)}
                          </p>
                          <If cond={!hasReportFilters}>
                            <Trend
                              growth={item?.orders_per_client?.growth || null}
                            />
                          </If>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatFloat(
                              item?.products_per_order?.value,
                              2
                            )}
                          </p>
                          {/* <If cond={!hasReportFilters}>
                            <Trend
                              growth={item?.products_per_order?.growth || null}
                            />
                          </If> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {Array.from({
                  length: Math.max(
                    0,
                    12 - data?.[selectedOption?.id || "units"]?.length
                  ),
                }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#1c1c1c",
                      }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </If>
      </div>
    </div>
  );
}
